// WO-20: games the UI offers. /api/games lists the active games (WO-14 turns a game on when its catalog is verified).
// Until that endpoint answers (or before migration 028), the UI is Pokemon only, exactly as before.
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export const DEFAULT_GAME = 'pokemon';

// Display names (the DB stores plain ASCII names).
const GAME_LABELS = {
  pokemon: 'Pokémon',
  yugioh: 'Yu-Gi-Oh!',
  mtg: 'Magic: The Gathering',
  lorcana: 'Disney Lorcana',
  onepiece: 'One Piece',
  swu: 'Star Wars: Unlimited',
};

const FALLBACK = [{ game_id: DEFAULT_GAME, name: 'Pokemon', active: true, counts: {} }];
let cache = null;
let inflight = null;

function loadGames() {
  if (cache) return Promise.resolve(cache);
  if (!inflight) {
    const apiUrl = process.env.REACT_APP_API_URL || '';
    inflight = fetch(`${apiUrl}/api/games`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        const list = Array.isArray(data?.results) && data.results.length ? data.results : FALLBACK;
        cache = list;
        return list;
      })
      .catch(() => FALLBACK)
      .finally(() => { inflight = null; });
  }
  return inflight;
}

export function gameLabel(gameId, games = null) {
  if (GAME_LABELS[gameId]) return GAME_LABELS[gameId];
  const g = (games || cache || []).find((x) => x.game_id === gameId);
  return g?.name || gameId || GAME_LABELS[DEFAULT_GAME];
}

export function useGames() {
  const [games, setGames] = useState(cache || FALLBACK);
  useEffect(() => {
    let alive = true;
    loadGames().then((list) => { if (alive) setGames(list); });
    return () => { alive = false; };
  }, []);
  return games;
}

// The game a browse/search page is showing: ?game=<id> when it names an active game, else Pokemon.
export function useCurrentGame() {
  const games = useGames();
  const [params, setParams] = useSearchParams();
  const requested = params.get('game');
  const game = requested && games.some((g) => g.game_id === requested) ? requested : DEFAULT_GAME;
  const setGame = (next) => {
    const p = new URLSearchParams(params);
    if (!next || next === DEFAULT_GAME) p.delete('game'); else p.set('game', next);
    setParams(p, { replace: true });
  };
  return { game, games, setGame, multi: games.length > 1 };
}

// The game a product / set id belongs to (D2 namespaces): ids without '.' and pkm.* / <pokemon set>.* are Pokemon.
const NS_TO_GAME = { pkm: 'pokemon', 'pkm-ja': 'pokemon', ygo: 'yugioh', mtg: 'mtg', lor: 'lorcana', op: 'onepiece', swu: 'swu' };
export function gameOfId(id) {
  const ns = String(id || '').split('.')[0];
  return String(id || '').includes('.') && NS_TO_GAME[ns] ? NS_TO_GAME[ns] : DEFAULT_GAME;
}

// WO-21: card ids served by /api/products/<id> (D2 namespaced singles: ygo.lob-en001, mtg.*, lor.*, op.*, swu.*,
// pkm.*, pkm-ja.*). Legacy Pokemon card ids never contain '.' (prod 2026-10-20: 0 of 20.7k cards), so
// /card/<legacy id> keeps reading /api/card/<id> exactly as before.
export function isProductCardId(id) {
  const s = String(id || '');
  return s.includes('.') && Object.prototype.hasOwnProperty.call(NS_TO_GAME, s.split('.')[0]);
}

// WO-26: search covers every active game unless the shopper picks one.
export const ALL_GAMES = 'all';

// Short names for result badges and the search dropdown.
const GAME_SHORT = { pokemon: 'Pokémon', yugioh: 'Yu-Gi-Oh!', mtg: 'MTG', lorcana: 'Lorcana', onepiece: 'One Piece', swu: 'SWU' };
export function gameShortLabel(gameId) {
  return GAME_SHORT[gameId] || gameLabel(gameId);
}

// The game the header search uses: ?game=<id> on the current page (search, series and sealed pages carry it),
// else all games. WO-26: the dropdown and Enter then match what the page shows.
export function headerSearchGame(search) {
  const g = new URLSearchParams(search || '').get('game');
  return g && g !== ALL_GAMES && (cache === null || cache.some((x) => x.game_id === g)) ? g : ALL_GAMES;
}

// The search page's game: ?game=<active game> searches that game only (as before WO-26); no param = all games
// (decided without waiting for /api/games, so the page searches once; with only Pokemon active, tcg=all answers the
// Pokemon list and the game tabs stay hidden).
export function useSearchGame() {
  const games = useGames();
  const [params, setParams] = useSearchParams();
  const requested = params.get('game');
  const multi = games.length > 1;
  // A requested game is trusted until /api/games has answered (else the page would search all games first and
  // then the game: two requests and the old answer could win); an inactive game then falls back to all games.
  const known = cache === null || games.some((g) => g.game_id === requested);
  const game = requested && requested !== ALL_GAMES && known ? requested : ALL_GAMES;
  const setGame = (next) => {
    const p = new URLSearchParams(params);
    if (!next || next === ALL_GAMES) p.delete('game'); else p.set('game', next);
    setParams(p, { replace: true });
  };
  return { game, games, setGame, multi };
}

// tcg= query part for /api/search*: all games -> tcg=all, Pokemon -> none (unchanged URLs), another game -> tcg=<id>.
export function searchTcgParam(game) {
  if (game === ALL_GAMES) return `&tcg=${ALL_GAMES}`;
  return game && game !== DEFAULT_GAME ? `&tcg=${encodeURIComponent(game)}` : '';
}

// Rows from a tcg=all answer carry their game; badges are only worth showing once a non-Pokemon row is present.
export function showGameBadges(rows, key = 'tcg') {
  return Array.isArray(rows) && rows.some((r) => r && r[key] && r[key] !== DEFAULT_GAME);
}

// fetch + json for a search URL. An API from before WO-26 does not know tcg=all (it answers with no `tcg` field and no
// results): then ask again for Pokemon, so a frontend deployed first still searches like today.
export async function fetchSearchJson(url, opts) {
  const r = await fetch(url, opts);
  if (!r.ok) throw new Error(`HTTP error! Status: ${r.status}`);
  const data = await r.json();
  const empty = !Array.isArray(data?.results) || data.results.length === 0;
  if (url.includes(`tcg=${ALL_GAMES}`) && data?.tcg !== ALL_GAMES && empty) {
    const r2 = await fetch(url.replace(`&tcg=${ALL_GAMES}`, '').replace(`tcg=${ALL_GAMES}&`, ''), opts);
    if (!r2.ok) throw new Error(`HTTP error! Status: ${r2.status}`);
    return r2.json();
  }
  return data;
}
